import { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import { formValueSelector, Field, FormSection } from 'redux-form'
import _ from 'lodash'
import cx from 'classnames'
import { Modal } from '@vzmi/shp_shared_compo'
import { COMMA } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import { CheckboxInputField } from '../../../Common/Form'
import { LISTING_QUERY_FORM_CONFIG } from '../../HOC/withListingQueryForm'
import styles from './index.css'
const FORM_SECTION_NAME = 'duplicatedForm'

const FIELD_PREFIX = 'duplicated_'

const getFieldName = id => `${FIELD_PREFIX}${id}`

// 取得共用商品賣場中被挑出來要批次更新的賣場
export const getSelectedDuplicatedListingIdMap = formValues => {
  const selectedDuplicatedListingIdMap = _.flow(
    () => _.keys(formValues),
    fields => _.filter(fields, field => _.startsWith(field, FIELD_PREFIX)),
    filteredFields => _.filter(filteredFields, field => formValues[field]),
    selectedFields => _.reduce(selectedFields, (map, field) => {
      const listingId = _.replace(field, FIELD_PREFIX, '')
      map[listingId] = true
      return map
    }, {})
  )()
  return selectedDuplicatedListingIdMap
}

// 取得共用商品賣場中被排除的賣場
export const getUnselectedDuplicatedListingIdMap = (duplicatedMap, formValues) => {
  const selectedDuplicatedIdMap = getSelectedDuplicatedListingIdMap(formValues)
  const unselectedListingIdMap = _.flow(
    () => _.keys(duplicatedMap),
    // 從按照 sku 分群含共用商品的賣場中取出全部賣場編號
    keys => _.reduce(keys, (map, key) => {
      const partListings = _.get(duplicatedMap, key, [])
      for (const { id } of partListings) {
        if (!(id in map)) {
          map[id] = true
        }
      }
      return map
    }, {}),
    // 全部含有共用商品的賣場扣掉被挑出來要一起更新的賣場，剩下的即為被排除的賣場
    duplicatedListingIdMap => _.omit(duplicatedListingIdMap, _.keys(selectedDuplicatedIdMap))
  )()
  return unselectedListingIdMap
}

// 取得不能選取的賣場
// duplicatedMap 從全部賣場中找出含有同樣商品(sku)的賣場
// selectedDuplicatedIdMap 共用商品賣場中被挑出來要批次更新的賣場
export const getDisabledListingIdMap = (duplicatedMap, selectedDuplicatedIdMap) => {
  const disabledListingIdMap = _.flow(
    // 取得所有對應重複賣場的 sku
    () => _.keys(duplicatedMap),
    skusStrList => _.map(skusStrList, skusStr => {
      const partListings = _.get(duplicatedMap, skusStr, [])
      const listingIds = _.map(partListings, 'id')
      // 不在挑出來要批次更新的對應表裡，代表是未選取的賣場
      const unselectedListingIds = _.filter(listingIds, id => !(id in selectedDuplicatedIdMap))
      // 同一個 sku 底下會有多個賣場，sku 全部賣場沒有跟未選取一樣多代表有賣場被選取了
      const isListingSelected = _.size(listingIds) !== _.size(unselectedListingIds)
      let disabledMap = {}
      // sku 下有賣場被選取時把剩下的賣場組成要 disabled 的對應表，
      // 這樣就能避免 user 又選到重複 sku 的賣場做修改
      if (isListingSelected) {
        disabledMap = _.reduce(listingIds, (map, id) => {
          map[id] = !(id in selectedDuplicatedIdMap)
          return map
        }, {})
      }
      return disabledMap
    }),
    // 每個 sku 各自有數個不同的賣場，但是 A 賣場可能跟 B 賣場重複了 sku 123，A 賣場跟 C 賣場重複了 sku 456，
    // 如果 sku 123 選擇了 B 那麼為了避免 sku 456 選到 A 就需要把兩個 sku 底下的賣場 A disabled
    // 因此才需要合併不同 sku 要 disabled 的賣場
    disabledMapList => _.merge(...disabledMapList)
  )()
  return disabledListingIdMap
}

class DuplicatedProductModal extends Component {
  onOk = () => {
    const {
      goToBatchUpdatePage,
      onCancel,
      duplicatedMap,
      selectedListingIds,
      formValues
    } = this.props
    const unselectedListingIdMap = getUnselectedDuplicatedListingIdMap(duplicatedMap, formValues)
    // 全部選取的賣場扣掉排除的賣場剩下的賣場再前往批次更新圖片頁面
    const filteredListingIds = _.filter(selectedListingIds, id => !(id in unselectedListingIdMap))
    goToBatchUpdatePage(filteredListingIds)
    onCancel()
  }

  render () {
    const {
      formValues,
      visible,
      width,
      onCancel,
      duplicatedMap
    } = this.props
    const selectedDuplicatedIdMap = getSelectedDuplicatedListingIdMap(formValues)
    const disabledListingIdMap = getDisabledListingIdMap(duplicatedMap, selectedDuplicatedIdMap)
    return (
      <Modal
        title='賣場含有共用商品'
        className={styles.modal}
        onOk={this.onOk}
        onCancel={onCancel}
        width={width}
        visible={visible}
      >
        <p
          className={cx(
            styles.hint,
            styles.title
          )}
        >
          批次修改暫不提供有共用商品之賣場修改，若未進行選擇，點擊「確定」後，將自動移除下列賣場並進入批次作業：
        </p>
        <br />
        {_.map(_.keys(duplicatedMap), skusStr => {
          const partListings = _.get(duplicatedMap, skusStr, [])
          const listingIds = _.map(partListings, 'id')
          return (
            <FormSection name={FORM_SECTION_NAME}>
              <p className={styles.hint}>
                賣場
                {` ${_.join(listingIds, COMMA)} `}
                有共用商品
                {` ${skusStr}`}
                ，無法同時批次修改
              </p>
              <p className={styles.hint}>
                請擇一進入批次修改
              </p>
              {partListings.map(({ id, name }) => {
                return (
                  <Fragment key={`${skusStr}_${id}`}>
                    <Field
                      name={getFieldName(id)}
                      component={CheckboxInputField}
                      className={styles.checkbox}
                      disabled={disabledListingIdMap[id]}
                    >
                      {id}
                      {' '}
                      {name}
                    </Field>
                    <br />
                  </Fragment>
                )
              })}
              <br />
            </FormSection>
          )
        })}
      </Modal>
    )
  }
}

const selector = formValueSelector(LISTING_QUERY_FORM_CONFIG.form)
const DuplicatedProductModalWithReduxForm = connect(state => {
  return {
    formValues: selector(state, FORM_SECTION_NAME) || {}
  }
})(DuplicatedProductModal)

export default DuplicatedProductModalWithReduxForm
